// frontend/src/features/media/FavoriteList.tsx
// FavoriteList — file-level favorites for a single media file
// Model: fav = { full: boolean, timestamps: number[] }
//
// - Click a timestamp  -> onJump(seconds)
// - Right-click/long-press a timestamp -> onRemove(seconds) (if provided)
// - Shows a chip for "Entire file favorited" with optional toggle
// - Design-agnostic: pass classes via props; no fixed colors/layout
// - A11y: aria labels, keyboard support (Delete/Backspace to remove)

import React, { useMemo, useRef } from 'react';
import { formatTimestamp } from '../../utils/formatters';

export type FileFavorite = {
  full?: boolean;
  timestamps?: number[];
};

export type FavoriteListProps = {
  fav?: FileFavorite;
  onJump: (seconds: number) => void;
  onRemove?: (seconds: number) => void;
  onToggleFull?: () => void;
  compact?: boolean;
  title?: string;
  className?: string;
  listClassName?: string;
  chipClassName?: string;
  fullChipClassName?: string;
};

const FavoriteList: React.FC<FavoriteListProps> = ({
  fav,
  onJump,
  onRemove,
  onToggleFull,
  compact = false,
  title,
  className,
  listClassName,
  chipClassName,
  fullChipClassName,
}) => {
  // Dedupe + sort + filter invalid
  const timestamps = useMemo<number[]>(() => {
    const arr = Array.isArray(fav?.timestamps) ? fav!.timestamps! : [];
    const uniq = Array.from(
      new Set(
        arr
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n) && n >= 0)
      )
    );
    return uniq.sort((a, b) => a - b);
  }, [fav?.timestamps]);

  const hasAny = Boolean(fav?.full) || timestamps.length > 0;
  if (!hasAny) return null;

  const label = title || 'Bookmarked moments';

  return (
    <section
      className={className}
      aria-label="Favorite timestamps"
      style={{ marginTop: compact ? 6 : 10 }}
    >
      <header style={{ marginBottom: compact ? 4 : 6 }}>
        <p
          style={{
            margin: 0,
            fontWeight: 700,
            fontSize: compact ? 12 : 14,
          }}
        >
          ⏱️ {label}
        </p>
      </header>

      {/* Timestamp chips */}
      {timestamps.length > 0 && (
        <div
          className={listClassName}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: compact ? 6 : 8,
          }}
        >
          {timestamps.map((t) => (
            <TimestampChip
              key={t}
              seconds={t}
              label={formatTimestamp(t)}
              compact={compact}
              onJump={onJump}
              onRemove={onRemove}
              className={chipClassName}
            />
          ))}
        </div>
      )}

      {/* Full favorite chip */}
      {fav?.full && (
        <div style={{ marginTop: timestamps.length ? (compact ? 6 : 8) : 0 }}>
          <FullFavoriteChip
            compact={compact}
            onToggleFull={onToggleFull}
            className={fullChipClassName}
          />
        </div>
      )}
    </section>
  );
};

/* --------------------------- Timestamp Chip --------------------------- */

type TimestampChipProps = {
  seconds: number;
  label: string;
  compact?: boolean;
  onJump: (seconds: number) => void;
  onRemove?: (seconds: number) => void;
  className?: string;
};

const TimestampChip: React.FC<TimestampChipProps> = ({
  seconds,
  label,
  compact,
  onJump,
  onRemove,
  className,
}) => {
  const longPressTimer = useRef<number | null>(null);
  const startPos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const LONG_PRESS_MS = 450;
  const MOVE_TOLERANCE = 8; // px

  const clearTimer = () => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!onRemove) return; // no long-press if removal is disabled
    startPos.current = { x: e.clientX ?? 0, y: e.clientY ?? 0 };
    clearTimer();
    longPressTimer.current = window.setTimeout(() => {
      longPressTimer.current = null;
      onRemove?.(seconds);
    }, LONG_PRESS_MS);
  };

  const handlePointerUp = () => {
    clearTimer();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!longPressTimer.current) return;
    const dx = (e.clientX ?? 0) - startPos.current.x;
    const dy = (e.clientY ?? 0) - startPos.current.y;
    if (Math.hypot(dx, dy) > MOVE_TOLERANCE) clearTimer();
  };

  return (
    <button
      type="button"
      onClick={() => onJump(seconds)}
      onContextMenu={(e) => {
        if (!onRemove) return;
        e.preventDefault();
        onRemove(seconds);
      }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={clearTimer}
      onPointerLeave={clearTimer}
      onPointerMove={handlePointerMove}
      onKeyDown={(e) => {
        if (!onRemove) return;
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          onRemove(seconds);
        }
      }}
      title={
        onRemove
          ? 'Click to jump · Right-click or long-press to remove'
          : 'Click to jump'
      }
      aria-label={`Jump to ${label}${onRemove ? ', hold to remove' : ''}`}
      className={className}
      style={{
        borderRadius: 8,
        padding: compact ? '4px 8px' : '6px 10px',
        fontSize: compact ? 12 : 13,
        cursor: 'pointer',
        // Design tokens should style colors/borders externally; keep minimal defaults:
        background: 'var(--chip-bg, #f5f5f5)',
        border: '1px solid var(--chip-border, #dcdcdc)',
      }}
    >
      ⏩ {label}
    </button>
  );
};

/* ------------------------ Full Favorite Chip ------------------------- */

type FullFavoriteChipProps = {
  compact?: boolean;
  onToggleFull?: () => void;
  className?: string;
};

const FullFavoriteChip: React.FC<FullFavoriteChipProps> = ({
  compact,
  onToggleFull,
  className,
}) => {
  return (
    <button
      type="button"
      onClick={() => onToggleFull?.()}
      onContextMenu={(e) => {
        if (!onToggleFull) return;
        e.preventDefault();
        onToggleFull();
      }}
      aria-pressed={true}
      aria-label="Entire file favorited"
      title={
        onToggleFull
          ? 'Click to toggle · Right-click to remove'
          : 'Entire file favorited'
      }
      className={className}
      style={{
        borderRadius: 8,
        padding: compact ? '4px 8px' : '6px 10px',
        fontSize: compact ? 12 : 13,
        cursor: onToggleFull ? 'pointer' : 'default',
        background: 'var(--fullchip-bg, #ffdfe2)',
        border: '1px solid var(--fullchip-border, #eab0b8)',
        color: 'var(--fullchip-fg, #8f1d2a)',
      }}
    >
      🧡 Entire file favorited
    </button>
  );
};

export default FavoriteList;
